/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import { MdaExtractMappingDetail } from "./types";

interface SortableRowProps {
  mapping: MdaExtractMappingDetail;
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onToggleEnabled: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const SortableRow = ({
  mapping,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onToggleEnabled,
  onEdit,
  onDelete,
}: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: mapping.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition ?? undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      component={Paper}
      variant="outlined"
      sx={{ mb: 1, alignItems: "center" }}
      secondaryAction={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Switch
            checked={mapping.enabled}
            onChange={onToggleEnabled}
            inputProps={{ "aria-label": `Enabled for ${mapping.sourceColumn}` }}
          />
          <Tooltip title="Edit">
            <IconButton aria-label="edit" onClick={onEdit} size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton aria-label="delete" onClick={onDelete} size="small">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ display: "flex", cursor: "grab", touchAction: "none" }}
          aria-label={`Drag to reorder ${mapping.sourceColumn}`}
        >
          <DragIndicatorIcon color="action" />
        </Box>
        <Stack direction="column">
          <IconButton
            aria-label="move up"
            size="small"
            onClick={onMoveUp}
            disabled={isFirst}
          >
            <ArrowUpwardIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            aria-label="move down"
            size="small"
            onClick={onMoveDown}
            disabled={isLast}
          >
            <ArrowDownwardIcon fontSize="inherit" />
          </IconButton>
        </Stack>
      </Stack>
      <ListItemText
        primary={mapping.sourceColumn}
        secondary={
          mapping.destinationColumn
            ? `→ ${mapping.destinationColumn}`
            : "No destination column set"
        }
      />
    </ListItem>
  );
};

export interface MappingListProps {
  mappings: MdaExtractMappingDetail[];
  onReorder: (orderedMappings: MdaExtractMappingDetail[]) => void;
  onToggleEnabled: (mapping: MdaExtractMappingDetail) => void;
  onEdit: (mapping: MdaExtractMappingDetail) => void;
  onDelete: (mapping: MdaExtractMappingDetail) => void;
}

const MappingList = ({
  mappings,
  onReorder,
  onToggleEnabled,
  onEdit,
  onDelete,
}: MappingListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = mappings.findIndex((m) => m.id === active.id);
      const newIndex = mappings.findIndex((m) => m.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onReorder(arrayMove(mappings, oldIndex, newIndex));
    },
    [mappings, onReorder],
  );

  const handleMove = React.useCallback(
    (index: number, direction: -1 | 1) => {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= mappings.length) return;
      onReorder(arrayMove(mappings, index, newIndex));
    },
    [mappings, onReorder],
  );

  if (mappings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No column mappings configured.
      </Typography>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={mappings.map((mapping) => mapping.id)}
        strategy={verticalListSortingStrategy}
      >
        <List sx={{ width: "100%" }}>
          {mappings.map((mapping, index) => (
            <SortableRow
              key={mapping.id}
              mapping={mapping}
              isFirst={index === 0}
              isLast={index === mappings.length - 1}
              onMoveUp={() => handleMove(index, -1)}
              onMoveDown={() => handleMove(index, 1)}
              onToggleEnabled={() => onToggleEnabled(mapping)}
              onEdit={() => onEdit(mapping)}
              onDelete={() => onDelete(mapping)}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
};

export default MappingList;
